import { Injectable } from '@jkhy/frontend-utils/di';
import {
    AchBatchesClient,
    AchPaymentsClient,
    TaxPaymentClient,
    StatesClient,
    AccountTypeModelDto,
    DeleteAchBatchModelDto,
    LookupModelDto,
    AchFederalTaxTemplateModelDto,
    AchStateTaxTemplateModelDto,
    AchFederalTaxPaymentModelDto,
    AchStateTaxPaymentModelDto,
    AchPaymentWithTemplatesModelDto,
    AchPaymentModelDto,
} from '@treasury/api/channel';
import { TaxTemplate } from './tax-template.entity';
import { TaxTemplateEditable } from './tax-template-editable.entity';
import { TaxPayment } from './tax-payment.entity';
import { TaxTemplateFilter, taxTemplateFilterToDto, TaxCode, TaxAmountType } from './types';

@Injectable()
export class TaxPaymentService {
    constructor(
        private readonly achBatchesClient: AchBatchesClient,
        private readonly achPaymentsClient: AchPaymentsClient,
        private readonly taxPaymentClient: TaxPaymentClient,
        private readonly statesClient: StatesClient
    ) {}

    public async searchTaxTemplates(filter: TaxTemplateFilter): Promise<TaxTemplate[]> {
        const dto = taxTemplateFilterToDto(filter);
        const response = await this.achBatchesClient.achBatchesSearchTaxTemplates(dto);
        return response.data.map(template => new TaxTemplate(template));
    }

    public async getTaxTemplate(id: number): Promise<TaxTemplateEditable> {
        const response = await this.achBatchesClient.achBatchesGet(id);
        return new TaxTemplateEditable(
            response.data.batch as AchFederalTaxTemplateModelDto | AchStateTaxTemplateModelDto
        );
    }

    public async createFederalTaxTemplate(
        template: AchFederalTaxTemplateModelDto
    ): Promise<TaxTemplateEditable> {
        const response = await this.achBatchesClient.achBatchesPostFederalTaxTemplate(template);
        return new TaxTemplateEditable(
            response.data.batch as AchFederalTaxTemplateModelDto | AchStateTaxTemplateModelDto
        );
    }

    public async createStateTaxTemplate(
        template: AchStateTaxTemplateModelDto
    ): Promise<TaxTemplateEditable> {
        const response = await this.achBatchesClient.achBatchesPostStateTaxTemplate(template);
        return new TaxTemplateEditable(
            response.data.batch as AchFederalTaxTemplateModelDto | AchStateTaxTemplateModelDto
        );
    }

    public async updateFederalTaxTemplate(
        id: number,
        template: AchFederalTaxTemplateModelDto
    ): Promise<TaxTemplateEditable> {
        const response = await this.achBatchesClient.achBatchesUpdateFederalTaxTemplate(
            id,
            template
        );
        return new TaxTemplateEditable(
            response.data.batch as AchFederalTaxTemplateModelDto | AchStateTaxTemplateModelDto
        );
    }

    public async updateStateTaxTemplate(
        id: number,
        template: AchStateTaxTemplateModelDto
    ): Promise<TaxTemplateEditable> {
        const response = await this.achBatchesClient.achBatchesUpdateStateTaxTemplate(id, template);
        return new TaxTemplateEditable(
            response.data.batch as AchFederalTaxTemplateModelDto | AchStateTaxTemplateModelDto
        );
    }

    public async cloneTaxTemplate(id: number): Promise<TaxTemplateEditable> {
        const response = await this.achBatchesClient.achBatchesClone(id);
        return new TaxTemplateEditable(
            response.data.batch as AchFederalTaxTemplateModelDto | AchStateTaxTemplateModelDto
        );
    }

    public async validateTemplateName(name: string): Promise<boolean> {
        const response = await this.achBatchesClient.achBatchesValidateTemplateName({
            templateName: name,
        });
        return response.data;
    }

    public async deleteTaxTemplate(id: number): Promise<void> {
        await this.achBatchesClient.achBatchesDelete({
            achBatchId: id,
        } as DeleteAchBatchModelDto);
    }

    public async approveTaxTemplate(id: number, comments?: string): Promise<void> {
        await this.achBatchesClient.achBatchesApprove(id, {
            value: comments,
        } as LookupModelDto);
    }

    public async rejectTaxTemplate(id: number, comments?: string): Promise<void> {
        await this.achBatchesClient.achBatchesReject(id, {
            value: comments,
        } as LookupModelDto);
    }

    public async initiateTaxTemplatesForEdit(templateIds: number[]): Promise<AchPaymentModelDto> {
        const lookups = templateIds.map(id => ({
            id,
            key: id.toString(),
        })) as LookupModelDto[];
        const response = await this.achBatchesClient.achBatchesInitiateTaxPayments(lookups);
        return response.data;
    }

    public async createTaxPaymentsFromTemplates(
        data: AchPaymentWithTemplatesModelDto
    ): Promise<void> {
        await this.achPaymentsClient.achPaymentsCreateTaxPaymentsFromTemplates(data);
    }

    public async createFederalTaxPayment(
        payment: AchFederalTaxPaymentModelDto
    ): Promise<TaxPayment> {
        const response = await this.achPaymentsClient.achPaymentsAchFederalTaxPayment(payment);
        return new TaxPayment(
            response.data as unknown as AchFederalTaxPaymentModelDto | AchStateTaxPaymentModelDto
        );
    }

    public async createStateTaxPayment(payment: AchStateTaxPaymentModelDto): Promise<TaxPayment> {
        const response = await this.achPaymentsClient.achPaymentsAchStateTaxPayment(payment);
        return new TaxPayment(
            response.data as unknown as AchFederalTaxPaymentModelDto | AchStateTaxPaymentModelDto
        );
    }

    public async getFederalTaxCodes(): Promise<TaxCode[]> {
        const response = await this.taxPaymentClient.taxPaymentGet({ format: 'json' });
        return response.data as unknown as TaxCode[];
    }

    public async getStateTaxCodes(stateId: number): Promise<TaxCode[]> {
        const response = await this.taxPaymentClient.taxPaymentGetStateTaxCodes(stateId, {
            format: 'json',
        });
        return response.data as unknown as TaxCode[];
    }

    public async getFederalTaxAmountTypes(taxCodeId: number): Promise<TaxAmountType[]> {
        const response = await this.taxPaymentClient.taxPaymentGetFederalTaxAmountTypes(
            { id: taxCodeId },
            { format: 'json' }
        );
        return response.data as unknown as TaxAmountType[];
    }

    public async getStateTaxAmountTypes(): Promise<TaxAmountType[]> {
        const response = await this.taxPaymentClient.taxPaymentGetStateTaxAmountTypes({
            format: 'json',
        });
        return response.data as unknown as TaxAmountType[];
    }

    public async getRecipientAccountTypes(): Promise<AccountTypeModelDto[]> {
        const response = await this.taxPaymentClient.taxPaymentGetTaxRecipientAccountTypes();
        return response.data;
    }

    public async getFederalTaxRecipient(): Promise<unknown> {
        const response = await this.taxPaymentClient.taxPaymentGeFederalTaxRecipient();
        return response.data;
    }

    public async getStates() {
        const response = await this.statesClient.statesGet();
        return response.data;
    }
}
